/**
 * Web Worker for encoding audio to 16-bit PCM WAV format.
 * Keeps the UI responsive during encoding of large files.
 */

// Typed self for Web Worker context
interface WorkerGlobalScope {
  onmessage: ((e: MessageEvent) => void) | null;
  postMessage(message: unknown, transfer?: Transferable[]): void;
}
declare const self: WorkerGlobalScope;

interface EncodeWavMessage {
  type: 'encode_wav';
  numChannels: number;
  sampleRate: number;
  frames: number;
  channels: ArrayBuffer[];
}

interface DoneMessage {
  type: 'done';
  payload: ArrayBuffer;
}

interface ErrorMessage {
  type: 'error';
  payload: string;
}

type WorkerMessage = DoneMessage | ErrorMessage;

function writeString(dataview: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i += 1) {
    dataview.setUint8(offset + i, str.charCodeAt(i));
  }
}

self.onmessage = (e: MessageEvent<EncodeWavMessage>) => {
  const { type } = e.data || {};
  if (type !== 'encode_wav') return;

  try {
    const { numChannels, sampleRate, frames, channels } = e.data;
    const bytesPerSample = 2;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const totalSamples = frames * numChannels;
    const dataSize = totalSamples * bytesPerSample;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const pcm16View = new Int16Array(buffer, 44, totalSamples);

    // RIFF/WAVE header
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // audio format (1 = PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // bits per sample
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // Convert Float32 to Int16 and interleave channels
    const channelArrays = channels.map(buf => new Float32Array(buf));
    for (let i = 0; i < frames; i += 1) {
      const base = i * numChannels;
      for (let c = 0; c < numChannels; c += 1) {
        let s = channelArrays[c][i];
        // Clamp to [-1, 1]
        if (s > 1) s = 1;
        else if (s < -1) s = -1;
        // Convert to 16-bit signed integer
        pcm16View[base + c] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
    }

    const response: DoneMessage = { type: 'done', payload: buffer };
    self.postMessage(response, [buffer]);
  } catch (err) {
    const response: ErrorMessage = {
      type: 'error',
      payload: err instanceof Error ? err.message : String(err),
    };
    self.postMessage(response);
  }
};

export type { EncodeWavMessage, WorkerMessage };
